import { Form, Formik } from "formik";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { DetailField } from "@volca/ui/components/ui/detail-field";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Label } from "@volca/ui/components/ui/label";
import { MaskedFormInput } from "@volca/ui/components/ui/masked-form-input";
import { MoneyInput } from "@volca/ui/components/ui/money-input";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@volca/ui/components/ui/tabs";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { numberOnlyMask } from "@volca/ui/masks";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { getAddressString } from "@/core/utils/address";
import { formatDateTimeWithZone } from "@/core/utils/date";
import { parseMaskedDollarInput } from "@/core/utils/money";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { getCustomerNameAndPhone } from "@/customer/utils";
import { ReferralActivityList } from "@/referrals/common/referral-activity-list";
import { SidebarHeader } from "@/referrals/common/sidebar-header";
import { IReferral, ReferralUpdateParams } from "@/referrals/referral";

interface TableSidebarProps {
  referral: IReferral | undefined;
  onClose: () => void;
  onUpdate: (values: Partial<ReferralUpdateParams>) => void;
  onLost?: () => void;
  onPayout: () => void;
  onDelete: () => void;
}

const serviceTitanJobIdMaskOptions = numberOnlyMask();

export const TableSidebar = ({
  referral,
  onClose,
  onUpdate,
  onLost,
  onPayout,
  onDelete,
}: TableSidebarProps) => {
  const navigate = useNavigate();
  const { businessId, selectedBusiness } = useBusiness();
  const { updateSidebar } = useBusinessSettingsSidebar();

  const [activeTab, setActiveTab] = useState("details");

  const isServiceTitanEnabled =
    selectedBusiness?.service_titan_enabled || false;

  const initalValues = useMemo(
    () => ({
      service_needed: referral?.service_needed || "",
      service_titan_job_id: referral?.service_titan_job_id?.toString() || "",
      deal_size: referral?.deal_size ? referral.deal_size.toString() : "",
      additional_info: referral?.additional_info || "",
    }),
    [referral],
  );

  return (
    <>
      {referral && (
        <Formik
          initialValues={initalValues}
          onSubmit={(values, { resetForm }) => {
            const parsedDealSize = parseMaskedDollarInput(values.deal_size);
            onUpdate({
              ...values,
              service_titan_job_id: values.service_titan_job_id
                ? parseInt(values.service_titan_job_id)
                : null,
              deal_size:
                parsedDealSize !== null && parsedDealSize > 0
                  ? parsedDealSize
                  : null,
            });
            resetForm({ values });
          }}
          enableReinitialize
        >
          {({ values, setFieldValue, getFieldProps, dirty, resetForm }) => (
            <Stack align="start" gap={0} className="h-full w-full">
              <Form className="flex h-full w-full flex-col">
                <SidebarHeader
                  referral={referral}
                  onClose={onClose}
                  onPayout={onPayout}
                  onLost={onLost}
                  onMoveToPaid={() => onUpdate({ status: "PAID" })}
                  hideIconPayoutButton
                  onDelete={onDelete}
                  actionButton={
                    <Button
                      size="sm"
                      type="button"
                      onClick={onPayout}
                      className="w-full"
                    >
                      Pay advocate
                    </Button>
                  }
                />
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="flex flex-1 flex-col gap-0 overflow-hidden pb-6 pl-6"
                >
                  <div className="border-b">
                    <TabsList variant="line" className="w-full">
                      <TabsTrigger
                        value="details"
                        className="flex-1"
                        onClick={() => setActiveTab("details")}
                      >
                        Details
                      </TabsTrigger>
                      <TabsTrigger
                        value="history"
                        className="flex-1"
                        onClick={() => setActiveTab("history")}
                      >
                        History
                      </TabsTrigger>
                    </TabsList>
                  </div>
                  <TabsContent value="details" className="min-h-0">
                    <div className="h-full w-full overflow-y-auto">
                      <Stack align="start" gap={6} className="w-full py-6 pr-6">
                        <FormInput
                          label="Services needed"
                          placeholder="Services needed"
                          {...getFieldProps("service_needed")}
                          fullWidth
                        />
                        <MoneyInput
                          name="deal_size"
                          label="Deal size"
                          value={values.deal_size}
                          onChange={(value) =>
                            setFieldValue("deal_size", value)
                          }
                          fullWidth
                        />
                        {isServiceTitanEnabled && (
                          <MaskedFormInput
                            name="service_titan_job_id"
                            label="ServiceTitan job ID"
                            placeholder="ServiceTitan job ID"
                            value={values.service_titan_job_id}
                            onChange={(value) =>
                              setFieldValue("service_titan_job_id", value)
                            }
                            maskOptions={serviceTitanJobIdMaskOptions}
                            fullWidth
                          />
                        )}
                        <Stack gap={2} className="w-full">
                          <Label htmlFor="referral-notes">Notes</Label>
                          <Textarea
                            id="referral-notes"
                            className="ml-px min-h-32"
                            placeholder="Notes"
                            {...getFieldProps("additional_info")}
                          />
                        </Stack>
                        <Stack align="start" direction="row" gap={2}>
                          <Button
                            type="submit"
                            variant="secondary"
                            size="sm"
                            disabled={!dirty}
                          >
                            Save changes
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            disabled={!dirty}
                            onClick={() => resetForm({ values: initalValues })}
                          >
                            Cancel
                          </Button>
                        </Stack>
                        <DetailField
                          label="Home address"
                          message={
                            getAddressString(referral.address) || "No address"
                          }
                        />
                        <DetailField
                          label="Advocate"
                          message={getCustomerNameAndPhone(referral.advocate)}
                          extraInfo="The customer who sent the referral (the one you pay out)"
                          onClick={() => {
                            void navigate(
                              `/business-settings/${businessId}/customers/${referral?.advocate?.status === CUSTOMER_STATUS.ACTIVE ? "advocates" : "leads"}`,
                            );
                            updateSidebar({
                              section: SIDEBAR_SECTIONS.advocates,
                              mode: SIDEBAR_MODES.view,
                              selectedItemId: referral?.advocate?.id,
                              isOpen: true,
                            });
                          }}
                        />
                        <DetailField
                          label="Referral SMS"
                          message={referral.text_message?.body || "No SMS"}
                          subMessage={
                            referral.created_at
                              ? formatDateTimeWithZone(referral.created_at)
                              : "No from"
                          }
                          extraInfo={`Original referral message sent to ${referral.business?.name} hotline by advocate`}
                        />
                      </Stack>
                    </div>
                  </TabsContent>
                  <TabsContent value="history" className="min-h-0">
                    <div className="h-full overflow-y-auto">
                      <ReferralActivityList referral={referral} />
                    </div>
                  </TabsContent>
                </Tabs>
              </Form>
            </Stack>
          )}
        </Formik>
      )}
    </>
  );
};
